import { JSONContentSchema } from '@littlebird/sdk';
import { z } from 'zod';

import {
  MeetingAudioEnergyLevelsPayload,
  CalendarEventAttendeeSchema,
  type StartMeetingInput,
  type TranscriptionEngine,
} from '../../../electron/shared/meetings.types.ts';

import { type TranscriptSyncManager } from './transcript-sync/TranscriptSyncManager.ts';

import type { RecordingSession } from './meetings.recording-session';

export { MeetingAudioEnergyLevelsPayload };

export const PLACEHOLDER_MEETING_TITLE = 'New Meeting';

export {
  CalendarEventsResponseSchema,
  EventType,
  buildCalendarEventFilterQueryParams,
  MeetingNotificationFilter,
  MeetingReminderLeadTime,
  MeetingMenubarIncludeEventsOption,
  MeetingMenubarPreviewLeadTimeOption,
  MeetingAutoStopTimeout,
  MeetingDetectionMode,
  getMeetingMenubarIncludeEventsDays,
  getMeetingMenubarIncludeEventsLabel,
  getMeetingMenubarPreviewLeadTimeLabel,
  getMeetingDetectionModeLabel,
  getMeetingProviderTitle,
  getMeetingProvider,
  getProviderFromBundleId,
  type CalendarEvent,
  type CalendarEventAttendee,
  type CalendarEventFilterSettings,
  type MeetingProvider,
  type TranscriptionEngine,
  type TranscriptDeltaPayload,
} from '../../../electron/shared/meetings.types.ts';

export type RecordingState =
  | { status: 'idle' }
  | {
      status: 'loading';
      meetingId: string;
      recordingSession?: RecordingSession;
      stopRenewingRecordingSession?: () => void;
      electronApiStartMeetingWasHit: boolean;
      nativeStartRequested?: boolean;
      activeTranscriptionEngine?: TranscriptionEngine;
      /** False until the start ack reveals the engine; latched from the
       * feature flag then so a mid-meeting flip cannot re-route this meeting. */
      usesLocalPostProcessing: boolean;
      transcriptManager: TranscriptSyncManager | null;

      unlistenTranscriptDelta: null | (() => void);
      /** Code-point boundary for suffix replacement; null keeps the live
       * transcript when an interrupted round's boundary is unknown */
      replaceWatermark: number | null;
    }
  | {
      status: 'recording';
      meetingId: string;
      recordingSession: RecordingSession;
      stopRenewingRecordingSession?: () => void;
      startedAt: Date;
      activeTranscriptionEngine?: TranscriptionEngine;
      usesLocalPostProcessing: boolean;
      transcriptManager: TranscriptSyncManager;
      transcriptLengthAtStart: number;
      /** Nullable code-point replace watermark (see the loading variant). */
      replaceWatermark: number | null;
      unlistenTranscriptDelta: () => void;
      unlistenPostStartNativeEvents: () => void;
    }
  | {
      status: 'stopping';
      meetingId: string;
      recordingSession?: RecordingSession;
      startedAt: Date;
      /** Carried from the recording state so stop-time deltas still route
       * through the engine-specific normalization. */
      activeTranscriptionEngine?: TranscriptionEngine;
      usesLocalPostProcessing: boolean;
      transcriptLengthAtStart: number;
      /** Nullable code-point replace watermark (see the loading variant). */
      replaceWatermark: number | null;
    };

export type StatusWindowRecordingState =
  | { status: 'idle' }
  | {
      status: 'recording';
      meetingId: string;
      startedAt: Date;
      activeTranscriptionEngine?: TranscriptionEngine;
      transcriptLengthAtStart: number;
    };

export type MeetingId = string;

export const MeetingChunkSchema = z.object({
  id: z.string().uuid(),
  chunk_number: z.number(),
  text: z.string(),
  created_at: z.string(),
});

export const MeetingSummaryStatusEnum = z
  .enum([
    'initial',
    'in_progress',
    'resummarizing',
    'completed',
    'no_transcript',
    'failed',
    'skipped',
  ])
  .nullable();

export const MeetingStatusEnum = z.enum(['active', 'ended']);

export const MeetingSchema = z.object({
  id: z.string().uuid(),
  created_at: z.string(),
  last_transcribed_at: z.string().nullable().optional(),
  updated_at: z.string().optional(),
  chunks: z.array(MeetingChunkSchema).optional(),
  summary: z.string().nullable(),
  meeting_app: z.string().optional(),
  name: z.string().nullable(),
  attendees: z.array(CalendarEventAttendeeSchema).nullable().optional(),
  speakers: z.array(z.string()).nullable(),
  personified_transcript: z.string().nullable(),
  tldr: z.string().nullable(),
  notes: z.nullable(JSONContentSchema),
  summary_status: MeetingSummaryStatusEnum,
  event_id: z.string().min(1).nullable().optional(),
  event_start_at: z.string().nullable().optional(),
  project_id: z.string().uuid().nullable().optional(),
  prep: z.string().nullable().optional(),
  source_app: z.string().nullable().optional(),
  external_source: z.string().nullable().optional(),
  status: MeetingStatusEnum.optional(),
  ended_at: z.string().nullable().optional(),
  first_chunk_timestamp: z.string().nullable().optional(),
  first_transcript_at: z.string().nullable().optional(),
  transcript_deleted_at: z.string().nullable().optional(),
});

export const MeetingNavigatePayload = z.string().uuid();

export const MeetingWindowFocusChangePayload = z.object({
  isFocused: z.boolean(),
});

export const MeetingStateChangePayload = z.array(
  z
    .object({
      id: z.string().uuid(),
    })
    .passthrough()
);

export type Meeting = z.infer<typeof MeetingSchema>;

export const SharedMeetingSummarySchema = z.object({
  id: z.string().uuid(),
  name: z.string().nullable(),
  summary: z.string().nullable(),
  created_at: z.string(),
  first_transcript_at: z.string().nullable().optional(),
  attendees: z.array(CalendarEventAttendeeSchema).nullable().optional(),
});
export type SharedMeetingSummary = z.infer<typeof SharedMeetingSummarySchema>;
export type MeetingWindowFocusChange = z.infer<
  typeof MeetingWindowFocusChangePayload
>;

/** Native audio energy level type */
export type MeetingAudioEnergyLevelNative = z.infer<
  typeof MeetingAudioEnergyLevelsPayload
>;

export type MeetingAudioEnergyLevel =
  MeetingAudioEnergyLevelNative['latestLevels'];

export function getMeetingTitle(meeting: Meeting) {
  return meeting.name ?? PLACEHOLDER_MEETING_TITLE;
}

export type MeetingStatusWindowMessage =
  | {
      type: 'ongoing';
      meeting: Meeting;
      activeTranscriptionEngine?: TranscriptionEngine;
    }
  | { type: 'patch_ongoing'; patch: Partial<Meeting> & { id: string } }
  | { type: 'no_ongoing_meeting' };

export type MeetingTab = 'notes' | 'summary' | 'transcript' | 'assist';

export type MeetingSidebarPanel = 'chat' | null;

export interface MeetingPermissions {
  mic: boolean;
}

export type MeetingPermissionType = 'microphone';

export const MeetingLanguageOptionSchema = z.object({
  code: z.string(),
  name: z.string(),
});

export type MeetingLanguageOption = z.infer<typeof MeetingLanguageOptionSchema>;

export const UpdateMeetingLanguagePreferenceResponseSchema = z.object({
  meeting_language_preference: z.string(),
});

export type UpdateMeetingLanguagePreferenceResponse = z.infer<
  typeof UpdateMeetingLanguagePreferenceResponseSchema
>;

export const MeetingShareDraftResponseSchema = z.object({
  draft_id: z.string(),
  toolkit_name: z.string(),
  account_label: z.string(),
});

export type MeetingShareDraftResponse = z.infer<
  typeof MeetingShareDraftResponseSchema
>;

// Re-export StartMeetingInput from shared types
export { type StartMeetingInput };
